/**
 * The header's own filter row — Category, Number and Date selectors, Esri
 * Operations Dashboard's "Selectors" reproduced in the header rather than as
 * a grid tile (see `DashboardSelector`'s own doc comment on why they are not
 * widgets). Renders in the builder's live Preview and in the published
 * Viewer — nowhere else, since a selector only means something once there is
 * data underneath it to narrow.
 *
 * Owns nothing beyond each control's OWN dropdown/calendar state; the actual
 * picked values live one level up (`DashboardViewer`/`DashboardPreview`'s
 * `selectorValues`), because that state has to survive this bar re-rendering
 * and be readable by `DashboardGrid` at the same time.
 */

import { useEffect, useState } from 'react';
import { getDataSourceAdapter } from '../services/dashboardDataService';
import type { DashboardDataSource, DashboardSelector } from '../types/dashboardTypes';
import type { SelectorValue } from '../utils/selectorRuntime';

// h-7, not the usual h-8/h-9 a standalone control would get: this sits beside
// the header's own title block (eyebrow + title, ~37px tall) and must not be
// the reason the header grows past that.
const controlClass = 'gl-input h-7 min-w-[9rem] px-2 py-0.5 text-xs';

/** A field's distinct values, fetched once per (source, field) pair and
 *  shared by every selector control that happens to reuse it. */
function useDistinctValues(
  source: DashboardDataSource | undefined,
  field: string,
): { values: string[]; loading: boolean } {
  const [values, setValues] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!source || !field) {
      setValues([]);
      return undefined;
    }
    let active = true;
    setLoading(true);
    void getDataSourceAdapter(source)
      .fetchDistinctValues(source, field)
      .then((result) => {
        if (active) setValues(result);
      })
      .catch(() => {
        if (active) setValues([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [source, field]);

  return { values, loading };
}

function CategoryControl({
  selector,
  source,
  value,
  onChange,
}: {
  selector: DashboardSelector;
  source?: DashboardDataSource;
  value: SelectorValue | undefined;
  onChange: (value: SelectorValue | undefined) => void;
}) {
  const { values, loading } = useDistinctValues(source, selector.field);
  const selected = value?.categoryValues ?? [];

  if (selector.multiple) {
    // `size={1}` keeps this the same closed-dropdown height as every other
    // control on this row — the header must not grow to fit it (see
    // DashboardSelectorBar's own note on why this whole bar lives in the
    // header's single row now). The trade-off: picking more than one value
    // needs Ctrl/Cmd-click, a native multi-select behaviour the title spells
    // out since nothing else on the control hints at it.
    return (
      <select
        multiple
        size={1}
        value={selected}
        onChange={(event) => {
          const picked = Array.from(event.target.selectedOptions, (option) => option.value);
          onChange(picked.length > 0 ? { categoryValues: picked } : undefined);
        }}
        aria-label={selector.label}
        title="Ctrl/Cmd-click to pick more than one"
        className={controlClass}
      >
        {values.map((entry) => (
          <option key={entry} value={entry}>
            {entry}
          </option>
        ))}
      </select>
    );
  }

  return (
    <select
      value={selected[0] ?? ''}
      onChange={(event) =>
        onChange(event.target.value ? { categoryValues: [event.target.value] } : undefined)
      }
      aria-label={selector.label}
      className={controlClass}
    >
      <option value="">{loading ? 'Loading…' : 'All'}</option>
      {values.map((entry) => (
        <option key={entry} value={entry}>
          {entry}
        </option>
      ))}
    </select>
  );
}

/** Distinct values, numeric ones only, sorted low → high. */
function useNumericValues(source: DashboardDataSource | undefined, field: string): number[] {
  const { values } = useDistinctValues(source, field);
  return values
    .map((entry) => Number(entry))
    .filter((entry) => Number.isFinite(entry))
    .sort((a, b) => a - b)
    .filter((entry, index, all) => index === 0 || entry !== all[index - 1]);
}

function NumberControl({
  selector,
  source,
  value,
  onChange,
}: {
  selector: DashboardSelector;
  source?: DashboardDataSource;
  value: SelectorValue | undefined;
  onChange: (value: SelectorValue | undefined) => void;
}) {
  const numbers = useNumericValues(source, selector.field);

  if (selector.mode === 'range') {
    return (
      <div className="flex items-center gap-1">
        <select
          value={value?.numberMin ?? ''}
          onChange={(event) => {
            const min = event.target.value === '' ? null : Number(event.target.value);
            const next = { ...value, numberMin: min };
            onChange(next.numberMin == null && next.numberMax == null ? undefined : next);
          }}
          aria-label={`${selector.label} — from`}
          className={`${controlClass} min-w-[6rem]`}
        >
          <option value="">Min</option>
          {numbers.map((entry) => (
            <option key={entry} value={entry}>
              {entry.toLocaleString()}
            </option>
          ))}
        </select>
        <span className="text-slate-400">–</span>
        <select
          value={value?.numberMax ?? ''}
          onChange={(event) => {
            const max = event.target.value === '' ? null : Number(event.target.value);
            const next = { ...value, numberMax: max };
            onChange(next.numberMin == null && next.numberMax == null ? undefined : next);
          }}
          aria-label={`${selector.label} — to`}
          className={`${controlClass} min-w-[6rem]`}
        >
          <option value="">Max</option>
          {numbers.map((entry) => (
            <option key={entry} value={entry}>
              {entry.toLocaleString()}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <select
      value={value?.numberSingle ?? ''}
      onChange={(event) =>
        onChange(event.target.value === '' ? undefined : { numberSingle: Number(event.target.value) })
      }
      aria-label={selector.label}
      className={controlClass}
    >
      <option value="">All</option>
      {numbers.map((entry) => (
        <option key={entry} value={entry}>
          {entry.toLocaleString()}
        </option>
      ))}
    </select>
  );
}

function DateControl({
  selector,
  value,
  onChange,
}: {
  selector: DashboardSelector;
  value: SelectorValue | undefined;
  onChange: (value: SelectorValue | undefined) => void;
}) {
  if (selector.mode === 'range') {
    return (
      <div className="flex items-center gap-1">
        <input
          type="date"
          value={value?.dateFrom ?? ''}
          onChange={(event) => {
            const from = event.target.value || null;
            const next = { ...value, dateFrom: from };
            onChange(next.dateFrom == null && next.dateTo == null ? undefined : next);
          }}
          aria-label={`${selector.label} — from`}
          className={`${controlClass} min-w-[8.5rem]`}
        />
        <span className="text-slate-400">–</span>
        <input
          type="date"
          value={value?.dateTo ?? ''}
          onChange={(event) => {
            const to = event.target.value || null;
            const next = { ...value, dateTo: to };
            onChange(next.dateFrom == null && next.dateTo == null ? undefined : next);
          }}
          aria-label={`${selector.label} — to`}
          className={`${controlClass} min-w-[8.5rem]`}
        />
      </div>
    );
  }

  return (
    <input
      type="date"
      value={value?.dateSingle ?? ''}
      onChange={(event) => onChange(event.target.value ? { dateSingle: event.target.value } : undefined)}
      aria-label={selector.label}
      className={`${controlClass} min-w-[8.5rem]`}
    />
  );
}

export default function DashboardSelectorBar({
  selectors,
  dataSources,
  values,
  onChange,
}: {
  selectors: DashboardSelector[];
  dataSources: DashboardDataSource[];
  values: Record<string, SelectorValue>;
  onChange: (id: string, value: SelectorValue | undefined) => void;
}) {
  const configured = selectors.filter((selector) => selector.field);

  return (
    // Fills whatever empty space is LEFT in the header's own single row —
    // between the title and the Refresh button, wherever that ends up once
    // the logo's own left/right position has taken its side (see
    // DashboardViewer / DashboardPreview, which render this as a `flex-1`
    // sibling of the title block and the logo in ONE row, not a row of its
    // own). `overflow-x-auto`, never wrap: several selectors scroll
    // sideways here rather than growing the header taller.
    <div className="flex min-w-0 flex-1 items-center gap-4 overflow-x-auto">
      {configured.map((selector) => {
        const source = dataSources.find((entry) => entry.id === selector.dataSourceId);
        const value = values[selector.id];
        const setValue = (next: SelectorValue | undefined) => onChange(selector.id, next);

        return (
          <label key={selector.id} className="flex flex-shrink-0 flex-col gap-0.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {selector.label}
            </span>
            {selector.type === 'category' && (
              <CategoryControl selector={selector} source={source} value={value} onChange={setValue} />
            )}
            {selector.type === 'number' && (
              <NumberControl selector={selector} source={source} value={value} onChange={setValue} />
            )}
            {selector.type === 'date' && (
              <DateControl selector={selector} value={value} onChange={setValue} />
            )}
          </label>
        );
      })}
    </div>
  );
}
